import type { AdjustmentKind } from '../model/types';
import type { CurvePoint } from './curves';
import { curveIsIdentity, identityCurve } from './curves';

/**
 * The adjustment catalogue.
 *
 * One table drives the panel, the stored parameters and the maths, so a control can
 * never disagree with what the filter does. Adding an adjustment means adding a row
 * — the menu, the panel, the palette and the preview all follow it.
 *
 * Most kinds take one number. Levels takes three and curves takes a drawn line, so
 * the session carries a *parameter set* rather than a single value: sliders keyed
 * by control, plus a curve for the kinds that draw one.
 */

export interface AdjustmentSpec {
  kind: AdjustmentKind;
  label: string;
  /** Multiplier from the stored normalised value to the number shown in the UI. */
  scale: number;
  unit: string;
  /** Kinds with no parameter ignore the stored value entirely. */
  parameterless: boolean;
  hint: string;
  /** Slider bounds in UI units; default is -scale..scale for signed kinds. */
  minUi?: number;
  maxUi?: number;
  /** Extra controls beyond the single legacy one, in UI units. */
  controls?: readonly ControlSpec[];
  /** True when the control is a drawn tone curve rather than sliders. */
  curve?: boolean;
}

/**
 * One slider.
 *
 * `scale` is what makes the legacy kinds fit without rewriting their maths: they
 * store a normalised -1..1 and show -100..100, so `stored = shown / scale`. New
 * kinds store what they show and use a scale of 1.
 */
export interface ControlSpec {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  neutral: number;
  unit: string;
  scale: number;
  /** Digits after the decimal point when the value is printed. */
  decimals?: number;
}

export const ADJUSTMENTS: readonly AdjustmentSpec[] = [
  {
    kind: 'brightness',
    label: 'Brightness',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Lift or drop every pixel',
  },
  {
    kind: 'contrast',
    label: 'Contrast',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Push darks and lights apart',
  },
  {
    kind: 'exposure',
    label: 'Exposure',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Photographic stops, ±2 EV',
  },
  {
    kind: 'saturation',
    label: 'Saturation',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Colour intensity',
  },
  {
    kind: 'hue',
    label: 'Hue',
    scale: 180,
    unit: '°',
    parameterless: false,
    hint: 'Rotate colours around the wheel',
  },
  {
    kind: 'sharpen',
    label: 'Sharpen',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Unsharp mask on edges',
    minUi: 0,
  },
  {
    kind: 'blur',
    label: 'Blur',
    scale: 40,
    unit: 'px',
    parameterless: false,
    hint: 'Gaussian blur radius',
    minUi: 0,
  },
  {
    kind: 'pixelate',
    label: 'Pixelate',
    scale: 64,
    unit: 'px',
    parameterless: false,
    hint: 'Mosaic in square blocks',
    minUi: 0,
  },
  {
    kind: 'noise',
    label: 'Noise',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Monochrome grain',
    minUi: 0,
  },
  {
    kind: 'vignette',
    label: 'Vignette',
    scale: 100,
    unit: '',
    parameterless: false,
    hint: 'Darken towards the edges',
    minUi: 0,
  },
  {
    kind: 'grayscale',
    label: 'Grayscale',
    scale: 100,
    unit: '',
    parameterless: true,
    hint: 'Drop colour, keep luminance',
  },
  {
    kind: 'sepia',
    label: 'Sepia',
    scale: 100,
    unit: '',
    parameterless: true,
    hint: 'Warm monochrome',
  },
  {
    kind: 'invert',
    label: 'Invert',
    scale: 100,
    unit: '',
    parameterless: true,
    hint: 'Negative of every channel',
  },
  {
    kind: 'levels',
    label: 'Levels',
    scale: 1,
    unit: '',
    parameterless: false,
    hint: 'Black point, white point and midtone gamma',
    controls: [
      {
        key: 'black',
        label: 'Blacks',
        min: 0,
        max: 250,
        step: 1,
        neutral: 0,
        unit: '',
        scale: 1,
      },
      {
        key: 'white',
        label: 'Whites',
        min: 5,
        max: 255,
        step: 1,
        neutral: 255,
        unit: '',
        scale: 1,
      },
      {
        key: 'gamma',
        label: 'Gamma',
        min: 0.1,
        max: 3,
        step: 0.01,
        neutral: 1,
        unit: '×',
        scale: 1,
        decimals: 2,
      },
    ],
  },
  {
    kind: 'curves',
    label: 'Curves',
    scale: 1,
    unit: '',
    parameterless: false,
    hint: 'Drag a tone curve: shadows left, highlights right',
    curve: true,
    controls: [],
  },
] as const;

export const ADJUSTMENT_BY_KIND: Record<AdjustmentKind, AdjustmentSpec> = Object.fromEntries(
  ADJUSTMENTS.map((spec) => [spec.kind, spec]),
) as Record<AdjustmentKind, AdjustmentSpec>;

/** The number a slider shows for a stored value. */
export function uiValue(kind: AdjustmentKind, value: number): number {
  const spec = ADJUSTMENT_BY_KIND[kind];
  return Math.round(value * spec.scale);
}

/** The value to store for a slider position. */
export function normalisedValue(kind: AdjustmentKind, ui: number): number {
  const spec = ADJUSTMENT_BY_KIND[kind];
  if (spec.parameterless) return 1;
  return ui / spec.scale;
}

/** Slider bounds in UI units. */
export function uiRange(kind: AdjustmentKind): { min: number; max: number } {
  const spec = ADJUSTMENT_BY_KIND[kind];
  return { min: spec.minUi ?? -spec.scale, max: spec.maxUi ?? spec.scale };
}

/** Neutral value meaning "no change" for a kind. */
export function neutralValue(kind: AdjustmentKind): number {
  return ADJUSTMENT_BY_KIND[kind].parameterless ? 1 : 0;
}

/* -------------------------------------------------------------------------- */
/* Parameters                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Everything an adjustment session needs to reproduce its look.
 *
 * Plain data on purpose: it goes into the store, it is compared on every slider
 * tick, and a curve is a list of points rather than a canvas. The legacy kinds use
 * one slider keyed `value`; levels uses three; curves uses the curve and no
 * sliders at all.
 */
export interface AdjustParams {
  sliders: Record<string, number>;
  curve: CurvePoint[];
}

/** The single slider of a legacy kind, derived from its row rather than repeated. */
function legacyControl(spec: AdjustmentSpec): ControlSpec {
  return {
    key: 'value',
    label: spec.label,
    min: spec.minUi ?? -spec.scale,
    max: spec.maxUi ?? spec.scale,
    step: 1,
    neutral: 0,
    unit: spec.unit || '%',
    scale: spec.scale,
  };
}

/** The sliders a kind offers, in display order. Empty for curve kinds. */
export function controlsOf(kind: AdjustmentKind): ControlSpec[] {
  const spec = ADJUSTMENT_BY_KIND[kind];
  if (spec.curve || spec.parameterless) return [];
  if (spec.controls) return [...spec.controls];
  return [legacyControl(spec)];
}

export function isCurveKind(kind: AdjustmentKind): boolean {
  return ADJUSTMENT_BY_KIND[kind].curve === true;
}

/** The parameters that mean "no change" — also what a fresh preview starts on. */
export function neutralParams(kind: AdjustmentKind): AdjustParams {
  const sliders: Record<string, number> = {};
  for (const control of controlsOf(kind)) sliders[control.key] = control.neutral;
  return { sliders, curve: isCurveKind(kind) ? identityCurve() : [] };
}

/** Clamp a parameter set into range, filling anything a caller left out. */
export function clampParams(kind: AdjustmentKind, params: AdjustParams): AdjustParams {
  const sliders: Record<string, number> = {};
  for (const control of controlsOf(kind)) {
    const raw = params.sliders[control.key];
    const value = Number.isFinite(raw) ? (raw as number) : control.neutral;
    sliders[control.key] = Math.min(control.max, Math.max(control.min, value));
  }
  return {
    sliders,
    curve: isCurveKind(kind) ? params.curve : [],
  };
}

/** True when the parameters would leave the layer exactly as it is. */
export function paramsAreNeutral(kind: AdjustmentKind, params: AdjustParams): boolean {
  // The parameterless kinds (grayscale, sepia, invert) have no parameters to be
  // neutral about: they always change the pixels, and "neutral" is only the value
  // they ignore. Calling them neutral would turn Apply into a no-op for invert.
  if (ADJUSTMENT_BY_KIND[kind].parameterless) return false;
  const neutral = neutralParams(kind);
  for (const key of Object.keys(neutral.sliders)) {
    if (Math.abs((params.sliders[key] ?? 0) - (neutral.sliders[key] ?? 0)) > 1e-6) return false;
  }
  if (isCurveKind(kind)) return curveIsIdentity(params.curve);
  return true;
}

/** Print a control's stored value the way the panel shows it. */
export function formatControl(control: ControlSpec, stored: number): string {
  const shown = stored * control.scale;
  return control.decimals ? shown.toFixed(control.decimals) : String(Math.round(shown));
}

/** Blur radius in pixels for a stored blur value, capped so a slider stays sane. */
export function blurRadius(value: number): number {
  return Math.max(0, Math.round(value * ADJUSTMENT_BY_KIND.blur.scale));
}

/** Mosaic block size in pixels for a stored pixelate value. */
export function pixelBlockSize(value: number): number {
  return Math.max(0, Math.round(value * ADJUSTMENT_BY_KIND.pixelate.scale));
}
